// Quick view builds its content from the fetched product page markup and then forces
// ONE gallery layout regardless of the media layout picked in Main product: a single
// static column on desktop, a slider with the shared progress-line pagination below
// GALLERY_SLIDER_BREAKPOINT. Every instance created per open is torn down in hide().
if (!customElements.get("quick-view-modal")) {
  const GALLERY_SLIDER_BREAKPOINT = 990;

  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        if (!this.modalContent) return;

        this.gallerySlider = null;
        this.galleryMediaQuery = null;
        this.onGalleryBreakpointChange = null;
        this.detailsObserver = null;
      }

      hide(preventFocus = false) {
        this.openedBy =
          this.openedBy
            ?.closest(".product-card")
            ?.querySelector(".product-card__name > a") || this.openedBy;
        if (preventFocus) this.openedBy = null;

        super.hide();

        this.teardownGallery();
        this.teardownDetails();
        this.removeAlertDrawer();

        this.addEventListener(
          "transitionend",
          () => {
            if (!this.hasAttribute("open")) {
              this.modalContent.innerHTML = "";
              this.classList.remove(
                "quick-view-modal--no-gallery",
                "quick-view-modal--details-overflow"
              );
            }
          },
          { once: true }
        );

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));

        // styles for forced disable body scroll on iOS
        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const btnSpinner = opener.querySelector(".button-loader");
        if (btnSpinner) btnSpinner.classList.remove("hidden");

        fetch(opener.dataset.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML();
            this.initSlider();
            this.initDetails();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);

            // styles for forced disable body scroll on iOS
            if (typeof window.lockScrollBodyOnIos === "function") {
              this.openScrollY = window.scrollY;
              window.lockScrollBodyOnIos(this.openScrollY);
            }
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          });
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements(this.productElement);
      }

      removeDOMElements(mainProductElement) {
        const selectors = [
          // Remove blocks from _product-blocks-under-gallery.liquid:
          ".product__gallery-blocks",
          // Remove blocks without specified data-attribute. A Hide on scroll group keeps
          // the attribute itself, so the blocks inside it are filtered one level deeper:
          ".product__info-container > :not([data-show-inside-quick-view='true'])",
          ".product__hide-on-scroll > :not([data-show-inside-quick-view='true'])",
          // Remove elements the forced gallery layout does not use:
          ".product__media-sublist-wrapper",
          ".product__media-progress",
          ".product__media-badges",
          ".product__media-toggle",
          ".product__media-mobile-icon",
          ".product__media-zoom-cursor",
          "product-modal",
          // Remove other blocks and elements:
          ".linked-products",
          ".product-popup",
          ".product-sticky-add-bar",
          ".pickup-availability",
          ".custom-liquid",
          ".complementary-products",
        ];
        const removingElements = mainProductElement.querySelectorAll(
          selectors.join(", ")
        );
        removingElements.forEach((el) => el.remove());

        const onlyOneBlockSelectors = [
          ".product-parameters",
          ".product__vendor",
          ".product__price",
          ".product__title",
          ".product__description",
          ".product__inventory",
          ".product__buy-buttons",
        ];

        onlyOneBlockSelectors.forEach((selector) => {
          const items = mainProductElement.querySelectorAll(selector);
          if (items.length > 1) {
            items.forEach((el, index) => {
              if (index > 0) el.remove();
            });
          }
        });
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;

        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );

        this.productElement
          .querySelectorAll("variant-picker")
          .forEach((variantPicker) => {
            variantPicker.dataset.originalSection = sectionId;
          });
      }

      setModalHTML() {
        this.modalContent.innerHTML = this.productElement.innerHTML;

        // With no media and the gallery placeholder switched off the product renders no
        // media column at all, so the panel narrows to the details column alone.
        this.classList.toggle(
          "quick-view-modal--no-gallery",
          !this.modalContent.querySelector(".product__media")
        );

        this.reinjectScriptTags();
        this.createFullDetailsButton();
        this.preventScrollBody();
        this.preventVariantURLSwitching();
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      createFullDetailsButton() {
        const infoContainer = this.modalContent.querySelector(
          ".product__info-container"
        );
        const fullDetailsBtnTemplate = this.querySelector(
          "[data-full-details-button]"
        );
        if (!infoContainer || !fullDetailsBtnTemplate) return;

        const fullDetailsBtn = fullDetailsBtnTemplate.content.cloneNode(true);

        infoContainer.appendChild(fullDetailsBtn);
      }

      preventScrollBody() {
        this.modalContent
          .querySelectorAll(
            ".product-form__controls-group input[type=radio], .dropdown-select__list input[type=radio]"
          )
          .forEach((input) => {
            input.addEventListener("focus", () => {
              const scrollTop =
                document.body.scrollTop || document.documentElement.scrollTop;
              setTimeout(() => {
                window.scrollTo(0, scrollTop);
              }, 0);
            });
          });
      }

      preventVariantURLSwitching() {
        const variantPickerEl =
          this.modalContent.querySelectorAll("variant-picker");

        variantPickerEl.forEach((el) => {
          el.setAttribute("data-update-url", "false");
        });
      }

      clearMediaClasses() {
        // Drops the gallery type/ratio classes of the product page so the quick view can
        // apply its own forced layout. A colour scheme class stays: the media tiles paint
        // their background from one (the gallery scopes it further in, on the list wrapper).
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter(
          (cssClass) =>
            keepClasses.includes(cssClass) || cssClass.startsWith("color-")
        );

        mediaWrapper.className = filteredClasses.join(" ");
        delete mediaWrapper.dataset.desktopType;
        delete mediaWrapper.dataset.mobileType;

        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );
        if (sliderEl) {
          sliderEl.classList.remove("swiper", "js-media-list");
          delete sliderEl.dataset.desktopType;
          delete sliderEl.dataset.mobileType;
          delete sliderEl.dataset.hasDesktopZoom;
          delete sliderEl.dataset.hasMobileZoom;
        }

        const sliderWrapper = this.modalContent.querySelector(
          ".product__media-list-container"
        );
        if (sliderWrapper) {
          sliderWrapper.classList.remove("swiper-wrapper");
        }

        this.modalContent
          .querySelectorAll(".product__media-item")
          .forEach((slide) => {
            slide.classList.remove("swiper-slide", "grid-active");
          });
      }

      // Re-entrant: variant-picker calls this again after it swaps the variant media.
      initSlider() {
        if (!this.modalContent) return;

        this.teardownGallery();
        this.clearMediaClasses();

        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );
        if (!sliderEl) return;

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        const paginationEl = sliderEl.querySelector(".slider-pagination");

        if (!sliderWrapper || slides.length < 2) {
          sliderEl.querySelector(".product__media-pagination")?.remove();
          return;
        }

        this.galleryMediaQuery = window.matchMedia(
          `(max-width: ${GALLERY_SLIDER_BREAKPOINT - 1}px)`
        );
        this.onGalleryBreakpointChange = () => {
          if (this.galleryMediaQuery.matches) {
            this.createGallerySlider(
              sliderEl,
              sliderWrapper,
              slides,
              paginationEl
            );
          } else {
            this.destroyGallerySlider(sliderEl);
          }
        };

        this.galleryMediaQuery.addEventListener(
          "change",
          this.onGalleryBreakpointChange
        );
        this.onGalleryBreakpointChange();
      }

      createGallerySlider(sliderEl, sliderWrapper, slides, paginationEl) {
        if (this.gallerySlider) return;

        sliderEl.classList.add("swiper");
        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        this.gallerySlider = new Swiper(sliderEl, {
          // Same geometry as the product page gallery (section-main-product.js): the
          // next image peeks so the swipe is discoverable, and the 2px seam separates
          // the outgoing and incoming slide. The slide width comes from CSS, hence
          // slidesPerView 'auto'.
          slidesPerView: "auto",
          spaceBetween: 2,
          autoHeight: false,
          loop: false,
          direction: "horizontal",
          speed: 800,
          allowTouchMove: true,
          watchSlidesProgress: true,
          mousewheel: {
            forceToAxis: true,
          },
          pagination: paginationEl
            ? {
                el: paginationEl,
                type: "progressbar",
              }
            : false,
          on: {
            slideChange: function () {
              if (typeof window.pauseAllMedia === "function") {
                window.pauseAllMedia(sliderEl);
              }
            },
          },
        });
      }

      destroyGallerySlider(sliderEl) {
        if (this.gallerySlider) {
          this.gallerySlider.destroy(true, true);
          this.gallerySlider = null;
        }

        if (!sliderEl) return;

        sliderEl.classList.remove("swiper");
        sliderEl
          .querySelector(".product__media-list-container")
          ?.classList.remove("swiper-wrapper");
        sliderEl.querySelectorAll(".product__media-item").forEach((slide) => {
          slide.classList.remove("swiper-slide");
          slide.removeAttribute("style");
        });
      }

      // The details column is sticky on desktop so it stays with the gallery, but sticky
      // pins the TOP: a column taller than the panel would put everything below the fold
      // out of reach (the last option dropdown included). Measured, so it follows a
      // variant change that adds or drops rows.
      initDetails() {
        this.teardownDetails();

        const wrapper = this.querySelector(
          ".quick-view-modal__content-wrapper"
        );
        const details = this.modalContent?.querySelector(
          ".product__info-container"
        );
        if (!wrapper || !details) return;

        const sync = () => {
          this.classList.toggle(
            "quick-view-modal--details-overflow",
            details.offsetHeight > wrapper.clientHeight
          );
        };

        this.detailsObserver = new ResizeObserver(sync);
        this.detailsObserver.observe(details);
        this.detailsObserver.observe(wrapper);
        sync();
      }

      teardownDetails() {
        if (!this.detailsObserver) return;
        this.detailsObserver.disconnect();
        this.detailsObserver = null;
      }

      // The alert drawer relocates itself to <body>, so it outlives the modal content it
      // came from and has to go with the modal that opened it.
      removeAlertDrawer() {
        document
          .querySelectorAll("back-in-stock-alert-drawer[data-quick-view]")
          .forEach((drawer) => drawer.remove());
      }

      teardownGallery() {
        if (!this.modalContent) return;

        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );

        this.destroyGallerySlider(sliderEl);

        if (this.galleryMediaQuery && this.onGalleryBreakpointChange) {
          this.galleryMediaQuery.removeEventListener(
            "change",
            this.onGalleryBreakpointChange
          );
        }

        this.galleryMediaQuery = null;
        this.onGalleryBreakpointChange = null;
      }

      // Called by variant-picker when the selected variant has its own media: the static
      // column pulls that tile to the top, the slider moves to its slide.
      setActiveMedia(mediaId) {
        if (!this.modalContent || !mediaId) return;

        // nested media nodes carry their own data-media-id, so match items only
        this.modalContent
          .querySelectorAll("[data-media-id].product__media-item")
          .forEach((item) => {
            item.classList.toggle(
              "quick-view-active",
              item.dataset.mediaId === mediaId
            );
          });

        if (!this.gallerySlider) return;

        const slideIndex = this.gallerySlider.slides.findIndex(
          (slide) => slide.dataset?.mediaId === mediaId
        );
        if (slideIndex !== -1) this.gallerySlider.slideTo(slideIndex);
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__link-overlay"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }
    }
  );
}
